import React, { useEffect, useState } from 'react';
import { SettingSystemUserHeader } from '../system-users/systemUserStyled';
import Heading from '../../../components/typography';
import { COLORS } from '../../../constants/style/color';
import { FMVContainer } from './fmv.styled';
import {
  useGetFmvConfigAwardType,
  useGetFmvConfigFmvType,
  useGetFmvConfigSettlementType,
  useGetFmvConfigTransactionType,
  useGetFmvConfigurationDetails,
  usePostFairMarketValueConfig,
  useWealthlaneAwardApi,
} from '../../../hooks';
import { FieldArray, Formik } from 'formik';
import FormSelect from '../../../components/dropdown/formSelect';
import InitialValuesForm from '../../../constants/initialValues';
import InputText from '../../../components/input-text/inputText';
import Button from '../../../components/button';
import { FormDivider } from '../system-roles/roles.styled';
import Spinners from '../../../components/spinner';
import { SkeletonLine } from '../../../components/skeleton';
import { toCamelCase } from '../../../utils/global';
import { FMV_CONFIGURATION_VALIDATION } from '../../../utils/validations/validations';
import _ from 'lodash';

interface FMVConfigurationFormValues {
  createGrantDateFmvDtos: Array<{
    awardTypeId: string;
    fmvTypeId: string;
  }>;
  createUpdateTransactionDateFmvDtos: Array<{
    transactionTypeId: string;
    fmvConfigurations: Array<{
      settlementTypeId: string;
      fmvTypeId: string;
      buffer: number;
    }>;
  }>;
}

const FmvConfiguration = () => {
  const { handleRequest } = useWealthlaneAwardApi();
  const { data: fmvConfigurationDetails, isLoading: detailsLoading } =
    useGetFmvConfigurationDetails(handleRequest);
  const { data: awardTypeList, status } = useGetFmvConfigAwardType(handleRequest);
  const { data: fmvTypeList, isLoading: fmvTypeListLoading } =
    useGetFmvConfigFmvType(handleRequest);
  const { data: transactionTypeList } = useGetFmvConfigTransactionType(handleRequest);
  const { data: settlementTypeList } = useGetFmvConfigSettlementType(handleRequest);
  const [initialValues, setInitialValues] = useState(InitialValuesForm.fmvConfiguration);
  useEffect(() => {
    if (awardTypeList && fmvConfigurationDetails && transactionTypeList) {
      // Create a map of existing grant date configurations for quick lookup
      const existingGrantConfigMap = new Map(
        fmvConfigurationDetails.createGrantDateFmvDtos?.map((config) => [
          config.awardTypeId,
          config.fmvTypeId,
        ]) || []
      );

      // Initialize with all award types, preserving existing FMV type selections
      const initializedGrantDateFmvDtos = awardTypeList.map((awardType) => ({
        awardTypeId: awardType.value,
        fmvTypeId: existingGrantConfigMap.get(awardType.value) || '',
      }));

      // Create a map of existing transaction configurations for quick lookup
      const existingTransactionConfigMap = new Map(
        fmvConfigurationDetails.createUpdateTransactionDateFmvDtos?.map((config) => [
          config.transactionTypeId,
          config.fmvConfigurations,
        ]) || []
      );

      // Initialize transaction date FMV configurations - only for Exercise
      const exerciseType = transactionTypeList?.find((type) => type.label === 'Exercise');

      const initializedTransactionDateFmvDtos = exerciseType
        ? [
            {
              transactionTypeId: exerciseType.value,
              fmvConfigurations:
                settlementTypeList?.map((settlementType) => {
                  const existingConfig = existingTransactionConfigMap
                    .get(exerciseType.value)
                    ?.find((config) => config.settlementTypeId === settlementType.value);

                  return {
                    settlementTypeId: settlementType.value,
                    fmvTypeId: existingConfig?.fmvTypeId || '',
                    buffer: existingConfig?.buffer || null,
                  };
                }) || [],
            },
          ]
        : [];

      setInitialValues((prev: any) => ({
        ...prev,
        createGrantDateFmvDtos: initializedGrantDateFmvDtos,
        createUpdateTransactionDateFmvDtos: initializedTransactionDateFmvDtos,
      }));
    }
  }, [awardTypeList, fmvConfigurationDetails, transactionTypeList, settlementTypeList]);

  const { mutate, isLoading } = usePostFairMarketValueConfig(handleRequest);

  return (
    <div data-page='fmvConfiguration' id='pageContainer'>
      <Formik<FMVConfigurationFormValues>
        enableReinitialize
        initialValues={initialValues}
        validationSchema={FMV_CONFIGURATION_VALIDATION}
        onSubmit={(values) => {
          mutate(values);
        }}>
        {({ touched, handleChange, errors, values, setFieldValue }) => {
          return (
            <>
              <SettingSystemUserHeader id='pageHeader'>
                <Heading variant='h3' title='FMV Configuration' id='fmvConfiguration' />
                <Heading
                  variant='body-s'
                  title='Configure your Fair Market Value'
                  color={COLORS.Gray}
                  id='pageSubtitle'
                />
              </SettingSystemUserHeader>
              <>
                {detailsLoading ? (
                  <FMVContainer id='loadingContainer'>
                    <SkeletonLine size='h2' width='300px' id='loadingTitle' />
                    <div className='grid grid-cols-4 mt-8' id='loadingGrantDateHeader'>
                      <SkeletonLine size='h3' width='200px' />
                      <SkeletonLine size='h3' width='250px' />
                    </div>
                    <div className='grid grid-cols-4 mt-[40px]' id='loadingGrantDateRow0'>
                      <SkeletonLine size='h4' width='200px' />
                      <SkeletonLine size='h4' width='250px' />
                    </div>
                    <div className='grid grid-cols-4 mt-[40px]' id='loadingGrantDateRow1'>
                      <SkeletonLine size='h4' width='200px' />
                      <SkeletonLine size='h4' width='250px' />
                    </div>
                    <div className='grid grid-cols-4 mt-[40px]' id='loadingGrantDateRow2'>
                      <SkeletonLine size='h4' width='200px' />
                      <SkeletonLine size='h4' width='250px' />
                    </div>
                    <div className='grid grid-cols-4 mt-[40px]' id='loadingGrantDateRow3'>
                      <SkeletonLine size='h4' width='200px' />
                      <SkeletonLine size='h4' width='250px' />
                    </div>

                    <FormDivider id='loadingGrantDateTransactionDivider' />
                    <SkeletonLine size='h2' width='300px' id='loadingTransactionTitle' />
                    <div className='grid md:grid-cols-4 gap-x-4 mt-8' id='loadingTransactionHeader'>
                      <SkeletonLine size='h3' width='200px' />
                      <SkeletonLine size='h3' width='200px' />
                      <SkeletonLine size='h3' width='200px' />
                      <SkeletonLine size='h3' width='200px' />
                    </div>

                    <div
                      className='grid md:grid-cols-4  gap-x-4 mt-[40px] items-center'
                      id='loadingTransactionRow0'>
                      <div>{<SkeletonLine size='h4' width='200px' />}</div>
                      <SkeletonLine size='h4' width='200px' />
                      <SkeletonLine size='h4' width='200px' />
                      <div className='px-2'>
                        <SkeletonLine size='h4' width='200px' />
                      </div>
                    </div>
                    <div
                      className='grid md:grid-cols-4  gap-x-4 mt-[40px] items-center'
                      id='loadingTransactionRow1'>
                      <div>{<SkeletonLine size='h4' width='200px' />}</div>
                      <SkeletonLine size='h4' width='200px' />
                      <SkeletonLine size='h4' width='200px' />
                      <div className='px-2'>
                        <SkeletonLine size='h4' width='200px' />
                      </div>
                    </div>
                    <div
                      className='grid md:grid-cols-4  gap-x-4 mt-[40px] items-center'
                      id='loadingTransactionRow2'>
                      <div>{<SkeletonLine size='h4' width='200px' />}</div>
                      <SkeletonLine size='h4' width='200px' />
                      <SkeletonLine size='h4' width='200px' />
                      <div className='px-2'>
                        <SkeletonLine size='h4' width='200px' />
                      </div>
                    </div>
                    <div
                      className='grid md:grid-cols-4  gap-x-4 mt-[40px] items-center'
                      id='loadingTransactionRow3'>
                      <div>{<SkeletonLine size='h4' width='200px' />}</div>
                      <SkeletonLine size='h4' width='200px' />
                      <SkeletonLine size='h4' width='200px' />
                      <div className='px-2'>
                        <SkeletonLine size='h4' width='200px' />
                      </div>
                    </div>
                  </FMVContainer>
                ) : (
                  <FMVContainer id='formContainer'>
                    <Heading
                      variant='h5'
                      title='Grant Date FMV'
                      color={COLORS?.Purple}
                      id='sectionTitleGrantDateFmv'
                    />
                    <div className='grid grid-cols-4 mt-8' id='grantDateFmvHeader'>
                      <Heading variant='h6' title='Award Type' id='columnAwardType' />
                      <Heading variant='h6' title='FMV Type' id='columnFmvType' />
                    </div>

                    <FieldArray
                      name='createGrantDateFmvDtos'
                      render={(arrayHelpers) => (
                        <>
                          {values.createGrantDateFmvDtos.map((item, index) => {
                            const awardType = awardTypeList?.find(
                              (award) => award.value === item.awardTypeId
                            );

                            return (
                              <div
                                key={index}
                                className='grid grid-cols-4 mt-4'
                                id={`row.${index}`}>
                                <Heading
                                  variant='body-s'
                                  title={awardType?.label}
                                  id={`cell.${index}.awardType`}
                                />
                                <FormSelect
                                  type='text'
                                  name={`createGrantDateFmvDtos.${index}.fmvTypeId`}
                                  id={`${toCamelCase(awardType?.label)}FmvTypeId`}
                                  containerClassName='formGroup'
                                  onChange={(e: any) => {
                                    const value = e?.target?.value || e;
                                    setFieldValue(
                                      `createGrantDateFmvDtos.${index}.fmvTypeId`,
                                      value
                                    );
                                  }}
                                  errors={errors}
                                  touched={touched}
                                  options={fmvTypeList}
                                  isLoading={fmvTypeListLoading}
                                  values={values}
                                  arrValue={values?.createGrantDateFmvDtos[index].fmvTypeId}
                                  fieldLabelName='fmvType'
                                />
                              </div>
                            );
                          })}
                        </>
                      )}
                    />
                    <FormDivider id='grantDateTransactionDivider' />
                    <Heading
                      variant='h5'
                      title='Transaction Date FMV'
                      className='mt-8'
                      color={COLORS?.Purple}
                      id='sectionTitleTransactionDateFmv'
                    />
                    <div className='grid md:grid-cols-4 gap-x-4 mt-8' id='transactionDateFmvHeader'>
                      <Heading variant='h6' title='Transaction Type' id='columnTransactionType' />
                      <Heading variant='h6' title='Settlement Type' id='columnSettlementType' />
                      <Heading variant='h6' title='FMV Type' id='columnTransactionFmvType' />
                      <Heading variant='h6' title='Buffer(%)' id='columnBuffer' />
                    </div>

                    <FieldArray
                      name='createUpdateTransactionDateFmvDtos'
                      render={(arrayHelpers) => (
                        <>
                          {values.createUpdateTransactionDateFmvDtos?.map(
                            (transactionItem: any, transactionIndex: any) => {
                              const transactionType = transactionTypeList?.find(
                                (type) => type.value === transactionItem.transactionTypeId
                              );
                              return (
                                <React.Fragment key={transactionIndex}>
                                  {transactionItem.fmvConfigurations.map(
                                    (settlementItem: any, settlementIndex: any) => {
                                      const settlementType = settlementTypeList?.find(
                                        (type) => type.value === settlementItem.settlementTypeId
                                      );

                                      // Determine the options for the FMV Type dropdown
                                      let currentFmvTypeList = fmvTypeList;
                                      if (
                                        settlementType?.label === 'Cash' ||
                                        settlementType?.label === 'Withhold To Cover'
                                      ) {
                                        currentFmvTypeList = fmvTypeList?.filter(
                                          (option) => option.label !== 'Sale Price'
                                        );
                                      }

                                      return (
                                        <div
                                          key={`${transactionIndex}-${settlementIndex}`}
                                          className='grid md:grid-cols-4  gap-x-4 mt-4 items-center'
                                          id={`row.${transactionIndex}.${settlementIndex}`}>
                                          <div
                                            id={`cell.${transactionIndex}.${settlementIndex}.transactionType`}>
                                            {settlementIndex === 0 && (
                                              <Heading
                                                variant='body-s'
                                                title={transactionType?.label}
                                                id={`transactionType.${transactionIndex}`}
                                              />
                                            )}
                                          </div>
                                          <Heading
                                            variant='body-s'
                                            title={settlementType?.label}
                                            id={`cell.${transactionIndex}.${settlementIndex}.settlementType`}
                                          />
                                          <FormSelect
                                            type='text'
                                            name={`createUpdateTransactionDateFmvDtos.${transactionIndex}.fmvConfigurations.${settlementIndex}.fmvTypeId`}
                                            // id={`createUpdateTransactionDateFmvDtos.${transactionIndex}.fmvConfigurations.${settlementIndex}.fmvTypeId`}
                                            id={`${toCamelCase(settlementType?.label)}FmvTypeId`}
                                            containerClassName='formGroup'
                                            onChange={(e: any) => {
                                              const value = e?.target?.value || e;
                                              setFieldValue(
                                                `createUpdateTransactionDateFmvDtos.${transactionIndex}.fmvConfigurations.${settlementIndex}.fmvTypeId`,
                                                value
                                              );
                                            }}
                                            errors={errors}
                                            touched={touched}
                                            options={currentFmvTypeList}
                                            isLoading={fmvTypeListLoading}
                                            values={values}
                                            arrValue={
                                              values?.createUpdateTransactionDateFmvDtos[
                                                transactionIndex
                                              ].fmvConfigurations[settlementIndex].fmvTypeId
                                            }
                                            fieldLabelName='fmvType'
                                            errorValue={
                                              (
                                                errors?.createUpdateTransactionDateFmvDtos?.[
                                                  transactionIndex
                                                ] as any
                                              )?.fmvConfigurations?.[settlementIndex]?.buffer
                                            }
                                            touchedValue={
                                              touched?.createUpdateTransactionDateFmvDtos?.[
                                                transactionIndex
                                              ]?.fmvConfigurations?.[settlementIndex]?.buffer
                                            }
                                            required
                                            disabled={settlementType?.label !== 'Sell To Cover'}
                                          />
                                          <div
                                            className='px-2'
                                            id={`cell.${transactionIndex}.${settlementIndex}.buffer`}>
                                            <InputText
                                              type='text'
                                              name={`createUpdateTransactionDateFmvDtos.${transactionIndex}.fmvConfigurations.${settlementIndex}.buffer`}
                                              id={`${toCamelCase(settlementType?.label)}Buffer`}
                                              onChange={(e: number) => {
                                                setFieldValue(
                                                  `createUpdateTransactionDateFmvDtos.${transactionIndex}.fmvConfigurations.${settlementIndex}.buffer`,
                                                  Number(e)
                                                );
                                                handleChange(e);
                                              }}
                                              errors={errors}
                                              touched={touched}
                                              errorValue={
                                                (
                                                  errors?.createUpdateTransactionDateFmvDtos?.[
                                                    transactionIndex
                                                  ] as any
                                                )?.fmvConfigurations?.[settlementIndex]?.buffer
                                              }
                                              touchedValue={
                                                touched?.createUpdateTransactionDateFmvDtos?.[
                                                  transactionIndex
                                                ]?.fmvConfigurations?.[settlementIndex]?.buffer
                                              }
                                              required
                                              disabled={settlementType?.label !== 'Sell To Cover'}
                                            />
                                          </div>
                                        </div>
                                      );
                                    }
                                  )}
                                </React.Fragment>
                              );
                            }
                          )}
                        </>
                      )}
                    />
                    <div className='flex justify-end gap-x-5 mt-8' id='formActions'>
                      <Button
                        title='Save'
                        type='submit'
                        loading={isLoading}
                        id='saveButton'
                        disabled={!_.isEmpty(errors)}
                        onClick={() => {
                          const payload = {
                            createGrantDateFmvDtos: values.createGrantDateFmvDtos.filter(
                              (createGrantDateFmvDto) => createGrantDateFmvDto.fmvTypeId !== ''
                            ),
                            createUpdateTransactionDateFmvDtos:
                              values?.createUpdateTransactionDateFmvDtos.map((data) => ({
                                transactionTypeId: data?.transactionTypeId,
                                fmvConfigurations: data?.fmvConfigurations
                                  .filter((fmvConfiguration) => fmvConfiguration.fmvTypeId !== '')
                                  .map((fmvConfigurationData) => ({
                                    buffer:
                                      Number(fmvConfigurationData?.buffer) === 0
                                        ? null
                                        : Number(fmvConfigurationData?.buffer),
                                    fmvTypeId: fmvConfigurationData?.fmvTypeId,
                                    settlementTypeId: fmvConfigurationData?.settlementTypeId,
                                  })),
                              })),
                          };
                          mutate(payload);
                        }}
                        variant='primary'
                        icon='check_circle'
                      />
                    </div>
                  </FMVContainer>
                )}
              </>
            </>
          );
        }}
      </Formik>
    </div>
  );
};

export default FmvConfiguration;
